import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Phone, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { AGE_GROUPS } from "@/lib/constants";
import ShelfRow from "@/components/ShelfRow";
import SubmitModal from "@/components/SubmitModal";
import BookDetailModal from "@/components/BookDetailModal";
import FeaturedSpotlight from "@/components/FeaturedSpotlight";

export default function Bookshelf() {
  const [books, setBooks] = useState([]);
  const [count, setCount] = useState(0);
  const [featured, setFeatured] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailBook, setDetailBook] = useState(null);

  const fetchBooks = async () => {
    try {
      const [booksRes, countRes, featuredRes] = await Promise.all([
        api.get("/books"),
        api.get("/books/count"),
        api.get("/books/featured"),
      ]);
      setBooks(booksRes.data);
      setCount(countRes.data.count);
      setFeatured(featuredRes.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const filtered = useMemo(() => {
    let list = books;
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(
        (b) =>
          b.title.toLowerCase().includes(s) ||
          b.author_name.toLowerCase().includes(s) ||
          b.description.toLowerCase().includes(s),
      );
    }
    if (activeFilter !== "all") {
      list = list.filter((b) => b.age_group === activeFilter);
    }
    return list;
  }, [books, search, activeFilter]);

  const byGroup = useMemo(() => {
    const map = {};
    AGE_GROUPS.forEach((g) => (map[g.value] = []));
    filtered.forEach((b) => {
      if (map[b.age_group]) map[b.age_group].push(b);
    });
    return map;
  }, [filtered]);

  const visibleGroups =
    activeFilter === "all" ? AGE_GROUPS : AGE_GROUPS.filter((g) => g.value === activeFilter);

  return (
    <main className="bg-page min-h-screen" data-testid="bookshelf-page">
      {/* Top nav bar */}
      <nav className="bg-white border-b border-[#E5E9EC]" data-testid="top-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4 flex items-center justify-between gap-4">
          <a
            href="https://www.thebookshelfproject.org"
            className="flex items-center gap-3"
            aria-label="The Bookshelf Project home"
            data-testid="logo-link"
          >
            <img
              src="/tbp-logo.png"
              alt="The Bookshelf Project"
              className="h-12 md:h-14 w-auto"
              loading="eager"
            />
          </a>
          <a
            href="https://www.zeffy.com/en-US/donation-form/f13a6f55-c455-4c81-ae9e-deb05b8c14af"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-teal px-5 py-2 rounded-full text-xs uppercase tracking-wider"
            data-testid="nav-donate"
          >
            Donate
          </a>
        </div>
      </nav>

      {/* Soft turquoise nav strip */}
      <div className="bg-[#38B6D2] border-y border-[#2A95B0]/50" data-testid="nav-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3 flex items-center justify-center md:justify-start gap-5 md:gap-9 text-[11px] md:text-sm font-extrabold uppercase tracking-[0.16em]">
          <a
            href="https://www.thebookshelfproject.org/about"
            className="text-[#F7C94B] hover:text-white transition-colors drop-shadow-[0_1px_0_rgba(14,35,46,0.55)]"
            data-testid="nav-about"
          >
            About
          </a>

          {/* Resources with dropdown */}
          <div className="relative group">
            <button
              type="button"
              className="text-[#F7C94B] hover:text-white transition-colors drop-shadow-[0_1px_0_rgba(14,35,46,0.55)] flex items-center gap-1"
              data-testid="nav-resources"
              aria-haspopup="true"
            >
              Resources
              <svg
                className="w-3 h-3 transition-transform group-hover:rotate-180"
                viewBox="0 0 12 8"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M6 8L0 0h12L6 8z" />
              </svg>
            </button>
            <div
              className="absolute left-1/2 -translate-x-1/2 top-full mt-1 min-w-[260px] bg-white rounded-lg shadow-xl border border-[#CFD6DC] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
              role="menu"
            >
              <a
                href="https://www.thebookshelfproject.org/the-science-of-reading"
                className="block px-5 py-3 text-xs uppercase tracking-wider font-bold text-[#0E232E] hover:bg-[#38B6D2]/15 hover:text-[#2A95B0] rounded-t-lg"
                data-testid="nav-science-of-reading"
                role="menuitem"
              >
                The Science of Reading
              </a>
              <a
                href="https://www.thebookshelfproject.org/early-literacy-professional-development"
                className="block px-5 py-3 text-xs uppercase tracking-wider font-bold text-[#0E232E] hover:bg-[#38B6D2]/15 hover:text-[#2A95B0] border-t border-[#E5E9EC] rounded-b-lg"
                data-testid="nav-early-literacy-pd"
                role="menuitem"
              >
                Early Literacy Professional Development
              </a>
            </div>
          </div>

          <a
            href="/"
            aria-current="page"
            className="text-white border-b-2 border-[#F7C94B] pb-0.5 drop-shadow-[0_1px_0_rgba(14,35,46,0.55)]"
            data-testid="nav-bookshelf"
          >
            The Bookshelf
          </a>
          <a
            href="https://www.thebookshelfproject.org/contact"
            className="text-[#F7C94B] hover:text-white transition-colors drop-shadow-[0_1px_0_rgba(14,35,46,0.55)]"
            data-testid="nav-contact"
          >
            Contact
          </a>
          <a
            href="https://www.thebookshelfproject.org/blog"
            className="text-[#F7C94B] hover:text-white transition-colors drop-shadow-[0_1px_0_rgba(14,35,46,0.55)]"
            data-testid="nav-blog"
          >
            Blog
          </a>
        </div>
      </div>

      {/* Header */}
      <header className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-10 md:pt-14 pb-8">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="inline-flex items-center gap-2 text-[#2B328C] font-bold text-[11px] uppercase tracking-[0.22em]">
              <span className="h-px w-6 bg-[#7EC8E3]" aria-hidden="true" />
              Discover · Authors · Stories
            </div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl font-extrabold text-[#0E232E] leading-[1.02] tracking-tight">
              The Bookshelf
            </h1>

            <div className="mt-6 space-y-5 text-[#2F2E2E] text-base md:text-[17px] leading-relaxed">
              <p>
                We've carefully curated a selection of captivating children's books by Black authors. Our collection is designed to provide representation, cultural enrichment, and educational value—empowering young readers and helping families build home libraries filled with diverse and meaningful literature.
              </p>

              <h2 className="pt-3 font-display text-2xl md:text-3xl font-extrabold text-[#0E232E] leading-tight tracking-tight">
                For Authors: Join The Bookshelf
              </h2>

              <p>
                The Bookshelf is more than a collection—it is a growing community rooted in purpose. This space is dedicated to Black children's authors who believe in the power of representation, early literacy, and building a love of reading in the home.
              </p>
              <p>
                If your work reflects the brilliance, culture, and lived experiences of Black children and families, we invite you to share your book with us. By submitting your title, you become part of a movement that places culturally relevant stories directly into the hands of children who need them most.
              </p>
              <p>
                Featured authors on The Bookshelf gain increased visibility within our community of families, educators, and literacy advocates, while contributing to a larger mission: ensuring every child has access to books that reflect who they are and who they can become.
              </p>
              <p className="text-[#0E232E] font-semibold">
                Explore our collection today—and if you're an author, add your voice to the shelf. Together, we are building more than libraries—we are building futures.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => setModalOpen(true)}
                  className="btn-teal px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm uppercase tracking-wider"
                  data-testid="submit-book-btn"
                  aria-label="Submit your book for review"
                >
                  <Plus size={16} /> Submit your book
                </button>
                <a
                  href="/my-books"
                  className="text-sm text-[#2B328C] hover:text-[#0E232E] font-semibold underline-offset-4 hover:underline"
                  data-testid="manage-books-link"
                >
                  Already submitted? Manage my books →
                </a>
              </div>
            </div>
          </div>

          <div className="relative lg:sticky lg:top-8">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#7EC8E3]/35 via-[#F7C94B]/25 to-[#EE9BA7]/30 rounded-[28px] blur-2xl" aria-hidden="true" />
            <img
              src="/hero-reading.png"
              alt="Five Black children laughing together while reading a stack of children's books on a colorful couch."
              className="relative w-full rounded-[24px] shadow-[0_30px_60px_-15px_rgba(14,35,46,0.35)] border-4 border-white"
              loading="eager"
              data-testid="hero-image"
            />
            <div className="absolute -bottom-4 -left-4 hidden md:flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg border border-[#CFD6DC]">
              <span className="h-2 w-2 rounded-full bg-[#38B6D2] animate-pulse" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-wider text-[#0E232E]">
                Curated for ages 0–8
              </span>
            </div>
          </div>
        </div>

        {/* Search + filter row */}
        <div className="mt-12 flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E232E]/60"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="Search titles, authors, themes…"
              className="field-input pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
              aria-label="Search books"
            />
          </div>
          <div
            className="flex gap-2 flex-wrap"
            role="tablist"
            aria-label="Filter by age group"
          >
            {[{ value: "all", label: "All shelves" }, ...AGE_GROUPS].map((g) => (
              <button
                key={g.value}
                role="tab"
                aria-selected={activeFilter === g.value}
                onClick={() => setActiveFilter(g.value)}
                className={`px-3.5 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold transition-all ${
                  activeFilter === g.value
                    ? "bg-[#38B6D2] text-white border border-[#2588A2] shadow-md"
                    : "bg-white border border-[#CFD6DC] text-[#0E232E] hover:border-[#38B6D2] hover:text-[#2A95B0]"
                }`}
                data-testid={`filter-${g.value}`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Featured Book of the Month */}
      <FeaturedSpotlight book={featured} onOpenDetail={setDetailBook} />

      {/* Shelves */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pb-16">
        {loading ? (
          <div className="text-[#0E232E]/60 italic text-lg py-20 text-center">
            Dusting the shelves…
          </div>
        ) : (
          <div className="flex flex-col gap-10 md:gap-12">
            {visibleGroups.map((g) => (
              <ShelfRow
                key={g.value}
                group={g}
                books={byGroup[g.value] || []}
                onOpenDetail={setDetailBook}
              />
            ))}
          </div>
        )}

        {/* Count footer */}
        <div
          className="mt-20 text-center text-[#0E232E]/60 italic text-sm tracking-wide"
          data-testid="book-count"
        >
          {count} {count === 1 ? "book" : "books"} on the shelf
        </div>
      </div>

      <SubmitModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={fetchBooks}
      />

      <BookDetailModal book={detailBook} onClose={() => setDetailBook(null)} />

      {/* Footer */}
      <footer className="bg-[#38B6D2] text-[#0E232E] mt-20" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-14">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <img
                src="/tbp-logo.png"
                alt="The Bookshelf Project"
                className="h-20 w-auto bg-white rounded-lg p-2 shadow-md"
                loading="lazy"
              />
              <p className="mt-4 text-sm text-[#0E232E]/85 leading-relaxed max-w-xs font-medium">
                Cultivating an early love for books, reading, and life-long learning — birth through age 8.
              </p>
            </div>
            <div>
              <h4 className="font-extrabold uppercase tracking-wider text-xs text-[#F7C94B] drop-shadow-[0_1px_0_rgba(14,35,46,0.55)]">
                About
              </h4>
              <p className="mt-3 text-sm text-[#0E232E] leading-relaxed font-medium">
                501(c)(3) Nonprofit
                <br />
                Federal Tax ID:{" "}
                <span className="font-bold">86-1326604</span>
                <br />
                <span className="text-[#0E232E]/75 italic">
                  Donations are tax deductible.
                </span>
              </p>
              <a
                href="https://www.zeffy.com/en-US/donation-form/f13a6f55-c455-4c81-ae9e-deb05b8c14af"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-[#F7C94B] text-sm font-extrabold uppercase tracking-wider hover:text-white transition-colors drop-shadow-[0_1px_0_rgba(14,35,46,0.55)]"
                data-testid="footer-donate-link"
              >
                Donate now →
              </a>
            </div>
            <div>
              <h4 className="font-extrabold uppercase tracking-wider text-xs text-[#F7C94B] drop-shadow-[0_1px_0_rgba(14,35,46,0.55)]">
                Contact
              </h4>
              <a
                href="tel:2029894125"
                className="mt-3 flex items-center gap-2 text-sm text-[#0E232E] font-semibold hover:text-[#2B328C] transition-colors"
                data-testid="footer-phone"
              >
                <Phone size={14} /> 202-989-4125
              </a>
              <a
                href="https://www.thebookshelfproject.org"
                className="mt-3 flex items-center gap-2 text-sm text-[#0E232E] font-semibold hover:text-[#2B328C] transition-colors"
                data-testid="footer-main-site"
              >
                <ExternalLink size={14} /> thebookshelfproject.org
              </a>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-[#0E232E]/15 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#0E232E]/65 font-medium">
            <span>© {new Date().getFullYear()} The Bookshelf Project. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <span>Made with care for Black children's literature.</span>
              <span aria-hidden="true" className="text-[#0E232E]/30">·</span>
              <a
                href="/admin/login"
                className="text-[#0E232E]/50 hover:text-[#0E232E] transition-colors underline-offset-2 hover:underline"
                data-testid="footer-admin-link"
              >
                Admin
              </a>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
